/**
 * Panel shown when /auth/session 401s.
 *
 * Previous behavior (vista#371/#372): the panel sent the user to
 * https://admin.vista-cloud.io with the assumption that CF Access at
 * admin would re-authenticate them and the resulting parent-domain
 * CF cookie would let them return to the org dashboard. That works
 * for vista_admin users but FAILS for org users — the admin worker
 * gates by `role === 'vista_admin'` and rejects org_admin / org_ro
 * with "Access Denied — restricted to Colorado Communications
 * administrators." Org users landing on this panel after a timeout
 * therefore had no recovery path; clicking the button just produced
 * a denial.
 *
 * Current behavior: redirect to the CURRENT origin. If CF Access
 * covers the dashboard's subdomain (the durable fix tracked by
 * sprint-13 #374), the user gets the CF login challenge inline,
 * authenticates, and comes back to the dashboard with a fresh
 * cookie + JWT — no admin-console detour. If CF Access does NOT
 * cover the subdomain, the user sees the same panel again rather
 * than being denied at admin; the secondary-text fallback tells
 * them what to do (contact org admin).
 *
 * Operator prerequisite (still): the durable fix is sprint-13
 * issue #374 (auto-provision CF Access apps on org create) so
 * every {slug}.vista-cloud.io subdomain is reliably covered. This
 * panel is the runtime recovery; the operator fix removes the need
 * for the secondary-text fallback case entirely.
 *
 * Lives in its own module so unit tests can SSR it without pulling
 * in App.jsx → AuthContext.jsx → api.js (vista#371 review note 3).
 */
export function SignInRequiredPanel() {
  // window may be undefined during SSR — guard the read.
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : '/';

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
        <h1 className="text-xl font-bold text-gray-900">Sign In Required</h1>
        <p className="mt-2 text-sm text-gray-600">
          Your session has expired. Sign in again to continue.
        </p>
        <a
          href={origin}
          className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Sign In
        </a>
        <p className="mt-6 text-xs text-gray-500">
          If signing in doesn&apos;t prompt for Cloudflare Access, contact
          your administrator — this subdomain may not be covered by the
          Cloudflare Access application policy yet.
        </p>
      </div>
    </div>
  );
}
